import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { changePassword as changePasswordAPI, getUser as getUserAPI, editMyPhone as editMyPhoneAPI, editMyEmail as editMyEmailAPI, getMe as getMeAPI, sendRecoverEmail as sendRecoverEmailAPI, UserDetails, oldLogin } from './AccountAPI';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

export interface AccountState {
  details: UserDetails | null;
  status: 'idle' | 'loading' | 'failed';
  error: Error | null;
  loadedAccounts: Record<string, UserDetails>;
  legacyToken: string | null;
}

const initialState: AccountState = {
  details: null,
  status: 'idle',
  error: null,
  loadedAccounts: {},
  legacyToken: null,
};

type AuthorizedActionArgs = {
  token: string;
}
type OldLoginArgs = {
  username: string;
  password: string;
}

export const tryOldLogin = createAsyncThunk<{ token: string }, OldLoginArgs>(
  'account/try_old_login',
  async ({ username, password }, { rejectWithValue }) => {
    for(let i = 0; i < 10; i++) {
      try{
        const response = await oldLogin(username, password);
        return response;
      }catch(error){
        if (i === 9) return rejectWithValue(error);
      }
    }
    return rejectWithValue(new Error("Failed to login after 10 attempts"));
  }
);
export const getMe = createAsyncThunk<UserDetails, AuthorizedActionArgs>(
  'account/get_me',
  async ({ token }, { rejectWithValue }) => {
    try{
      const response = await getMeAPI(token);
      return response;
    }catch(error){
      return rejectWithValue(error);
    }
  }
);
export const getUser = createAsyncThunk<UserDetails, { id: string } & AuthorizedActionArgs>(
  'account/get_user',
  async ({ id, token }, { rejectWithValue, getState }) => {
    try{
      const state = await getState() as RootState;
      const alreadyLoaded = getLoadedUser(id)(state);
      if(!alreadyLoaded){
        const response = await getUserAPI(id, token);
        return response;
      }else
        return alreadyLoaded;
    }catch(error){
      return rejectWithValue(error);
    }
  }
);
export const sendRecoverEmail = createAsyncThunk(
  'account/send_recover_email',
  async (email: string, { rejectWithValue }) => {
    try{
      const response = await sendRecoverEmailAPI(email);
      return response;
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

type ChangePasswordRequest = {
  newPassword: string;
  oldPassword: string;
}

export const changePassword = createAsyncThunk<void, ChangePasswordRequest & AuthorizedActionArgs>(
  'account/change_password',
  async (
    {newPassword, oldPassword, token},
    { rejectWithValue, getState, dispatch }
  ) => {
    try{
      const state = await getState() as RootState;
      const me = getLoggedAccount(state);
      if(!me) throw new Error("Utente non ancora caricato");
      await changePasswordAPI(newPassword, oldPassword, token);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);
export const editMyPhone = createAsyncThunk<Partial<UserDetails>, { telephone: string } & AuthorizedActionArgs>(
  'account/edit_my_phone',
  async ({ telephone, token }, { rejectWithValue, getState }) => {
    try{
      const state = await getState() as RootState;
      const me = getLoggedAccount(state);
      if(!me) throw new Error("Utente non ancora caricato");
      return await editMyPhoneAPI(telephone, me.id, token);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);
export const editMyEmail = createAsyncThunk<Partial<UserDetails>, { email: string } & AuthorizedActionArgs>(
  'account/edit_my_email',
  async ({ email, token }, { rejectWithValue }) => {
    try{
      return await editMyEmailAPI(email, token);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);
export const logout = createAsyncThunk(
  'account/logout',
  async () => {
    return;
  }
);

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(getUser.fulfilled, (state, action: PayloadAction<UserDetails>) => {
        state.status = 'idle';
        state.loadedAccounts[action.payload.id] = action.payload;
      })
      .addCase(
        changePassword.fulfilled,
        (state) => {
          state.status = 'idle';
        }
      )
      .addCase(
        tryOldLogin.fulfilled,
        (state, action: PayloadAction<{ token: string }>) => {
          state.status = 'idle';
          state.legacyToken = action.payload.token;
        }
      )
      .addMatcher(
        isAnyOf(getMe.fulfilled, editMyPhone.fulfilled, editMyEmail.fulfilled),
        (state, action: PayloadAction<Partial<UserDetails>>) => {
          state.status = 'idle';
          state.details = {
            ...state.details,
            ...action.payload
          } as UserDetails;
        }
      )
      .addMatcher(
        isAnyOf(
          getMe.pending, sendRecoverEmail.pending, editMyPhone.pending,
          editMyEmail.pending, getUser.pending, changePassword.pending,
          tryOldLogin.pending
        ),
        (state) => {
          state.status = 'loading';
          state.error = null;
        }
      )
      .addMatcher(
        isAnyOf(
          getMe.rejected, sendRecoverEmail.rejected, editMyPhone.rejected,
          editMyEmail.rejected, getUser.rejected, changePassword.rejected,
          tryOldLogin.rejected
        ),
        (state, action) => {
          state.status = 'failed';
          const error = action.payload;
          state.error = (error instanceof Error) ? error : new Error(error as string);
        }
      );
  },
});

export const isLoading = (state: RootState) => state.account.status === 'loading';
export const getError = (state: RootState) => state.account.error;
export const getLoggedAccount = (state: RootState) => state.account.details;
export const getLoadedUser =
  (email: string) =>
    (state: RootState): UserDetails | undefined => state.account.loadedAccounts[email];
export const getLegacyToken = (state: RootState): string | null => state.account.legacyToken;

const persistConfig = {
  key: 'account',
  storage,
  whitelist: ['legacyToken']
};

export default persistReducer(persistConfig, accountSlice.reducer);
